import { Check, Copy } from "lucide-react";
import { useState } from "react";

import type { CreateApiKeyResponse } from "@/modules/dashboard/settings/services/apiKeysService";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useTranslations } from "@/shared/lang/useTranslations";

export interface ApiKeySecretBannerProps {
  created: CreateApiKeyResponse;
  onDismiss: () => void;
}

/**
 * The secret, shown once.
 *
 * `plainKey` is returned by `POST /api-keys` and by nothing else — every later read is masked — so this is
 * the only moment it exists on screen, and there is no endpoint to recover it. That is why it renders **on
 * the page** rather than inside the creation drawer: an overlay closes on Escape or on a click outside, and
 * dismissing it before copying would destroy the credential with no way back. Here it stays until dismissed
 * deliberately.
 */
export function ApiKeySecretBanner({ created, onDismiss }: ApiKeySecretBannerProps) {
  const { t } = useTranslations("settings");
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(created.plainKey)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  return (
    <ColocaAlert variant="warning" heading={t("api_keys_created_banner_title")}>
      <p className="text-[12px] text-muted-foreground">{t("api_keys_created_banner_warning")}</p>
      <div className="mt-2 flex items-center gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto rounded bg-muted px-2.5 py-2 font-mono text-[12px] text-foreground">
          {created.plainKey}
        </code>
        <ColocaButton variant="default" size="xs" icon={copied ? Check : Copy} onClick={handleCopy}>
          {copied ? t("api_keys_created_copied") : t("api_keys_created_copy")}
        </ColocaButton>
      </div>
      <ColocaButton variant="ghost" size="xs" className="mt-2" onClick={onDismiss}>
        {t("api_keys_created_dismiss")}
      </ColocaButton>
    </ColocaAlert>
  );
}
